/* ==========================================
   PRODUCTION CONTACT FORM HANDLER & TOAST SYSTEM
   Field-level validation, loading states, API hook, & toasts
   ========================================== */

import { saveConsultation } from './firebase.js';

export function showToast(message, type = 'success', duration = 5000) {
  let toastContainer = document.getElementById('toast-container');
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'toast-container';
    toastContainer.className = 'toast-container';
    document.body.appendChild(toastContainer);
  }

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;

  const iconSvg = type === 'success' 
    ? `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`
    : type === 'error'
    ? `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EF4444" stroke-width="2.5"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`
    : `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" stroke-width="2.5"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>`;

  toast.innerHTML = `
    <span>${iconSvg}</span>
    <span style="flex: 1; line-height: 1.4;">${message}</span>
  `;

  toastContainer.appendChild(toast);

  // Trigger smooth slide-in
  requestAnimationFrame(() => {
    toast.classList.add('show');
  });

  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 400);
  }, duration);
}

export function initContactForm() {
  const form = document.getElementById('contact-form');
  if (!form) return;

  const nameInput = document.getElementById('contact-name');
  const emailInput = document.getElementById('contact-email');
  const serviceInput = document.getElementById('contact-track');
  const messageInput = document.getElementById('contact-message');

  const nameError = document.getElementById('name-error');
  const emailError = document.getElementById('email-error');
  const messageError = document.getElementById('message-error');
  const formAlert = document.getElementById('form-alert');

  const submitBtn = document.getElementById('contact-submit-btn');
  const submitText = document.getElementById('submit-btn-text');
  const submitIcon = document.getElementById('submit-btn-icon');

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Real-time input validation reset
  [nameInput, emailInput, messageInput].forEach((input) => {
    if (!input) return;
    input.addEventListener('input', () => {
      input.style.borderColor = '';
      if (input === nameInput && nameError) nameError.style.display = 'none';
      if (input === emailInput && emailError) emailError.style.display = 'none';
      if (input === messageInput && messageError) messageError.style.display = 'none';
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    let isValid = true;

    // Validate Name
    if (!nameInput.value.trim()) {
      isValid = false;
      nameInput.style.borderColor = '#f43f5e';
      if (nameError) nameError.style.display = 'block';
    } else {
      if (nameError) nameError.style.display = 'none';
    }

    // Validate Email
    if (!emailInput.value.trim() || !emailRegex.test(emailInput.value.trim())) {
      isValid = false;
      emailInput.style.borderColor = '#f43f5e';
      if (emailError) emailError.style.display = 'block';
    } else {
      if (emailError) emailError.style.display = 'none';
    }

    // Validate Message
    if (!messageInput.value.trim() || messageInput.value.trim().length < 10) {
      isValid = false;
      messageInput.style.borderColor = '#f43f5e';
      if (messageError) messageError.style.display = 'block';
    } else {
      if (messageError) messageError.style.display = 'none';
    }

    if (!isValid) {
      showToast('Please correct the highlighted fields before submitting.', 'error');
      return;
    }

    // Show loading state
    const originalText = submitText ? submitText.textContent : 'Submit Consultation Request';
    if (submitBtn) submitBtn.disabled = true;
    if (submitText) submitText.textContent = 'Transmitting Consultation Scope...';
    if (submitIcon) submitIcon.textContent = '⏳';
    if (formAlert) formAlert.style.display = 'none';

    const payload = {
      name: nameInput.value.trim(),
      email: emailInput.value.trim(),
      service: serviceInput ? serviceInput.value : 'General',
      message: messageInput.value.trim(),
      timestamp: new Date().toISOString()
    };

    try {
      // Dispatch lead payload directly to Firebase Firestore
      const result = await saveConsultation(payload);
      console.log('[Backend] Consultation stored in Firebase Firestore:', result);

      // Success feedback
      form.reset();
      if (formAlert) {
        formAlert.className = 'form-alert success';
        formAlert.innerHTML = '✔ <strong>Inquiry Received!</strong> Our Principal Architect or Doctoral Advisor will review your brief and contact you within 2 hours.';
        formAlert.style.display = 'block';
      }

      showToast('Consultation request dispatched successfully! We will contact you within 2 hours.', 'success');
    } catch (err) {
      console.error('Contact Form Error:', err);
      if (formAlert) {
        formAlert.className = 'form-alert error';
        formAlert.innerHTML = '✖ <strong>Transmission issue:</strong> Unable to dispatch at this moment. Please email directly at <a href="mailto:contact@wahitservices.com" style="text-decoration: underline;">contact@wahitservices.com</a>.';
        formAlert.style.display = 'block';
      }
      showToast('Network error while sending request. Please reach out via email.', 'error');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
      if (submitText) submitText.textContent = originalText;
      if (submitIcon) submitIcon.textContent = '→';
    }
  });
}
